import { useState } from 'react';
import { acceptInvite } from '../services/api';
import { useT } from '../i18n';

interface InviteProps {
  token: string;
  onGoLogin: () => void;
}

export default function Invite({ token, onGoLogin }: InviteProps) {
  const t = useT();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t('invite.errorPassword'));
      return;
    }
    if (!consent) {
      setError(t('invite.errorConsent'));
      return;
    }
    setLoading(true);
    try {
      await acceptInvite(token, name || undefined, password, consent);
      setSuccess(true);
      // Limpar URL apos processar o convite
      window.history.replaceState({}, '', '/');
    } catch (err: any) {
      if (err?.status === 404) setError(t('invite.invalidToken'));
      else if (err?.status === 410) setError(t('invite.expiredToken'));
      else setError(err instanceof Error ? err.message : t('invite.error'));
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <section className="mx-auto max-w-md rounded-lg border border-brand-200 bg-white p-8 shadow-sm text-center">
        <h1 className="text-2xl font-bold text-brand-900">{t('invite.successTitle')}</h1>
        <p className="mt-4 text-sm text-brand-600">{t('invite.successMessage')}</p>
        <button
          type="button"
          onClick={onGoLogin}
          className="mt-6 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
        >
          {t('invite.goLogin')}
        </button>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-md rounded-lg border border-brand-200 bg-white p-8 shadow-sm">
      <h1 className="mb-1 text-2xl font-bold text-brand-900">{t('invite.title')}</h1>
      <p className="mb-6 text-sm text-brand-600">{t('invite.subtitle')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium text-brand-700">
            {t('invite.name')}
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none"
            placeholder={t('invite.namePlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-brand-700">
            {t('invite.password')}
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-brand-500">{t('invite.passwordHint')}</p>
        </div>

        <div className="flex items-start gap-2">
          <input
            id="consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-brand-300 text-brand-600 focus:ring-brand-500"
          />
          <label htmlFor="consent" className="text-sm text-brand-600">
            {t('invite.consent')}
          </label>
        </div>

        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-brand-700 px-4 py-2 font-medium text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {loading ? t('common.loading') : t('invite.submit')}
        </button>
      </form>
    </section>
  );
}
